/**
 * WidgetGallery — Phase 3.
 *
 * Three optional widgets the user can toggle on/off from the
 * Quick Actions panel:
 *   1. Heat Map           — period × axis intensity grid (reuses the
 *                           heatmap data already returned by /insights).
 *   2. Gender Donut       — pie chart over the `gender` (or `sex`)
 *                           disaggregation axis when present.
 *   3. Target vs Achievement Bar — horizontal bar per numeric indicator
 *                           comparing latest value to target.
 *
 * Toggle state persists in localStorage so users don't have to re-pick
 * every visit. Each widget falls back gracefully when its source data
 * isn't available in the current slice.
 */
import { useEffect, useMemo, useState } from "react";
import { Flame, Users, Target, Sparkles, Check, X, MapPin } from "lucide-react";
import api from "../lib/api";
import GeoMapView from "./GeoMapView";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const WIDGETS = [
  { key: "heatmap", label: "Heat Map", icon: Flame, blurb: "Period × axis intensity grid for any auto-discovered disaggregation." },
  { key: "geo_map", label: "Geographic map (Nigeria)", icon: MapPin, blurb: "Drillable Country → State → LGA choropleth of any numeric indicator." },
];

const STORAGE_KEY = "ae-widget-gallery-v1";

export function useWidgetGallery() {
  const [active, setActive] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : { heatmap: false, geo_map: false };
    } catch {
      return { heatmap: false, geo_map: false };
    }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(active)); } catch { /* localStorage quota */ }
  }, [active]);
  return [active, setActive];
}

export function WidgetGalleryDialog({ open, onClose, active, onChange }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="widget-gallery-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-600" /> Widget gallery
          </DialogTitle>
          <DialogDescription>
            Pick the widgets you want pinned to your dashboard. Choices save locally and stay across visits.
          </DialogDescription>
        </DialogHeader>
        <ul className="mt-2 space-y-2" data-testid="widget-gallery-list">
          {WIDGETS.map((w) => {
            const Icon = w.icon;
            const on = !!active[w.key];
            return (
              <li
                key={w.key}
                className={`border rounded-sm p-3 flex items-start gap-3 cursor-pointer transition-colors ${
                  on ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200 bg-white hover:border-slate-400"
                }`}
                onClick={() => onChange({ ...active, [w.key]: !on })}
                data-testid={`widget-gallery-row-${w.key}`}
              >
                <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${on ? "text-emerald-600" : "text-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{w.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{w.blurb}</p>
                </div>
                <div className={`w-5 h-5 rounded-sm border shrink-0 flex items-center justify-center ${
                  on ? "bg-emerald-500 border-emerald-500" : "border-slate-300"
                }`}>
                  {on && <Check className="w-3 h-3 text-white" />}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="flex items-center justify-end pt-3 border-t border-slate-100">
          <Button onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="widget-gallery-close">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------
// Target vs Achievement bar — horizontal stacked bars per indicator.
// --------------------------------------------------------------------------
export function TargetVsAchievementWidget({ programIds }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.post("/dashboard/insights", { program_ids: programIds?.length ? programIds : null })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [programIds]);
  const rows = useMemo(() => {
    if (!data?.kpi_tiles) return [];
    return data.kpi_tiles
      .filter((t) => t.target != null && t.latest_value != null && t.target > 0)
      .slice(0, 12)
      .map((t) => ({
        code: t.code,
        name: t.name,
        latest: t.latest_value,
        target: t.target,
        pct: Math.min(t.latest_value / t.target * 100, 200),
        rag: t.rag,
      }));
  }, [data]);
  if (!data) return null;
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="widget-target-vs">
        <p className="label-overline flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-emerald-600" /> Target vs Achievement</p>
        <p className="text-xs text-slate-500 mt-2">No indicators with both a latest value and target in this slice.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="widget-target-vs">
      <p className="label-overline flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-emerald-600" /> Target vs Achievement</p>
      <ul className="mt-3 space-y-2.5">
        {rows.map((r) => (
          <li key={r.code} data-testid={`widget-tva-row-${r.code}`}>
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="font-mono text-slate-700 truncate" title={r.name}>{r.code}</span>
              <span className="text-slate-500">
                <span className="font-mono">{r.latest.toLocaleString()}</span>
                <span className="opacity-60"> / {r.target.toLocaleString()}</span>
                <span className={`ml-2 font-medium ${r.pct >= 95 ? "text-emerald-700" : r.pct >= 70 ? "text-amber-700" : "text-rose-700"}`}>
                  {r.pct.toFixed(0)}%
                </span>
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-sm relative overflow-hidden">
              <div
                className={`h-full rounded-sm ${r.pct >= 100 ? "bg-emerald-500" : r.pct >= 70 ? "bg-amber-500" : "bg-rose-500"}`}
                style={{ width: `${Math.min(r.pct, 100)}%` }}
              />
              {/* Overshoot indicator when pct > 100 */}
              {r.pct > 100 && (
                <div
                  className="absolute top-0 right-0 h-full bg-emerald-700 opacity-60"
                  style={{ width: `${Math.min(r.pct - 100, 100)}%` }}
                  title="Exceeded target"
                />
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --------------------------------------------------------------------------
// Gender Donut — uses the auto-discovered axis when gender/sex present.
// --------------------------------------------------------------------------
export function GenderDonutWidget({ programIds }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.post("/dashboard/insights", { program_ids: programIds?.length ? programIds : null })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [programIds]);
  const slices = useMemo(() => {
    if (!data?.axes) return [];
    const ax = data.axes.find((a) => /^(gender|sex)$/.test(a.axis));
    return ax?.slices?.slice(0, 8) || [];
  }, [data]);
  const total = slices.reduce((sum, s) => sum + (s.value || 0), 0);
  if (!data) return null;
  if (slices.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="widget-gender">
        <p className="label-overline flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-emerald-600" /> Gender Donut</p>
        <p className="text-xs text-slate-500 mt-2">No <span className="font-mono">gender</span> or <span className="font-mono">sex</span> axis detected in this slice.</p>
      </div>
    );
  }
  // CSS conic-gradient donut — no chart lib needed for a 6-slice pie.
  const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9", "#84cc16", "#f43f5e"];
  let cumulative = 0;
  const stops = slices.map((s, i) => {
    const start = (cumulative / total) * 100;
    cumulative += s.value;
    const end = (cumulative / total) * 100;
    return `${COLORS[i % COLORS.length]} ${start}% ${end}%`;
  }).join(", ");
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="widget-gender">
      <p className="label-overline flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-emerald-600" /> Gender Donut</p>
      <div className="flex items-center gap-4 mt-3">
        <div
          className="w-24 h-24 rounded-full shrink-0 relative"
          style={{ background: `conic-gradient(${stops})` }}
        >
          <div className="absolute inset-3 rounded-full bg-white grid place-items-center">
            <span className="font-mono text-[10px] text-slate-500">{total.toLocaleString()}</span>
          </div>
        </div>
        <ul className="flex-1 min-w-0 space-y-1 text-xs">
          {slices.map((s, i) => (
            <li key={s.label} className="flex items-center gap-2" data-testid={`widget-gender-slice-${s.label}`}>
              <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
              <span className="truncate flex-1 text-slate-700">{s.label}</span>
              <span className="font-mono text-slate-500">{((s.value / total) * 100).toFixed(0)}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Heat Map — period × first auto-discovered axis that has a heatmap.
// --------------------------------------------------------------------------
export function HeatMapWidget({ programIds }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.post("/dashboard/insights", { program_ids: programIds?.length ? programIds : null })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [programIds]);
  const hm = useMemo(() => {
    const ax = (data?.axes || []).find((a) => a.heatmap);
    return ax ? { axis: ax.axis, ...ax.heatmap } : null;
  }, [data]);
  if (!data) return null;
  if (!hm) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="widget-heatmap">
        <p className="label-overline flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-emerald-600" /> Heat Map</p>
        <p className="text-xs text-slate-500 mt-2">Heat-map needs at least two periods + a disaggregation axis with data.</p>
      </div>
    );
  }
  const max = Math.max(1, ...hm.cells.map((c) => c.value || 0));
  const intensity = (v) => {
    if (!v) return "rgba(16,185,129,0.06)";
    const a = 0.15 + (v / max) * 0.75;
    return `rgba(16,185,129,${a.toFixed(2)})`;
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 overflow-x-auto" data-testid="widget-heatmap">
      <p className="label-overline flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-emerald-600" /> Heat Map <span className="font-mono normal-case tracking-normal text-slate-400">· {hm.axis}</span></p>
      <table className="mt-3 text-[11px] border-collapse">
        <thead>
          <tr>
            <th className="text-left text-slate-500 pr-2 font-medium">{hm.axis}</th>
            {hm.periods.map((p) => (
              <th key={p} className="text-slate-500 px-1.5 font-mono font-medium">{p}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hm.rows.map((r) => (
            <tr key={r}>
              <td className="pr-2 text-slate-700 truncate max-w-[120px]" title={r}>{r}</td>
              {hm.periods.map((p) => {
                const cell = hm.cells.find((c) => c.row === r && c.period === p);
                const v = cell?.value || 0;
                return (
                  <td key={p} className="px-1 py-1">
                    <div
                      className="w-12 h-7 rounded-sm border border-slate-100 flex items-center justify-center font-mono text-[10px] text-slate-700"
                      style={{ background: intensity(v) }}
                      title={`${r} · ${p}: ${v}`}
                    >
                      {v ? (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0)) : "—"}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --------------------------------------------------------------------------
// Geo Map widget — compact embedded version of the full /map page.
// --------------------------------------------------------------------------
export function GeoMapWidget({ programIds }) {
  return <GeoMapView programIds={programIds} compact />;
}

